import React, { useState, useEffect } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom'; // Added useLocation
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faSearch,
  faBookmark,
  faStar,
  faStarHalfAlt,
  faArrowLeft,
  faSlidersH,
  faClock,
} from '@fortawesome/free-solid-svg-icons';
import { faStar as faStarRegular } from '@fortawesome/free-regular-svg-icons';
import Header from './components/Header';
import AddToListModal from './AddtoListModal';

function CocktailsListPage({ userData, lists, apiClient, onLogout, onUpdateLists }) {
  const [cocktails, setCocktails] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedCocktail, setSelectedCocktail] = useState(null);
  const navigate = useNavigate();
  const { listid, typeid } = useParams();
  const location = useLocation(); // Added to access the passed state
  const IMAGE_BASE_URL = '/img/cocktails/';

  useEffect(() => {
    const fetchCocktails = async () => {
      try {
        let payload;
        if (listid) {
          // Handle the /cocktails/:listid route
          payload =
            listid === '0'
              ? { userid: userData.userid, filter: { type: 'favorites' } }
              : { userid: userData.userid, filter: { type: 'list', listid: parseInt(listid) } };
        } else if (typeid) {
          // Handle the /cocktails/type/:typeid route
          payload = {
            userid: userData.userid,
            filter: {
              type: 'ingredient_type',
              typeid: parseInt(typeid),
            },
          };
        } else {
          throw new Error('Invalid route parameters');
        }

        const data = await apiClient.request('/api/cocktails', {
          method: 'POST',
          body: JSON.stringify(payload),
        });
        setCocktails(data || []);
      } catch (err) {
        setError(err.message || 'Failed to fetch cocktails');
        if (err.message === 'Failed to refresh token') {
          onLogout();
          navigate('/');
        }
      } finally {
        setLoading(false);
      }
    };

    if (userData && userData.userid && userData.access_token) {
      fetchCocktails();
    } else {
      setError('User data is incomplete');
      setLoading(false);
    }
  }, [userData, listid, typeid, apiClient, onLogout, navigate]);

  // Use the spiritName from the location state, or fallback to a generic name
  const spiritName = location.state?.spiritName || 'Spirit';
  const listName = typeid
    ? `${spiritName} Cocktails`
    : listid === '0'
    ? 'Favorites'
    : lists.find((l) => l.listid === parseInt(listid))?.listname || 'List';

  const renderStars = (rating) => {
    const fullStars = Math.floor(rating);
    const hasHalfStar = rating % 1 >= 0.5;
    const stars = [];
    for (let i = 0; i < 5; i++) {
      if (i < fullStars) {
        stars.push(<FontAwesomeIcon key={i} icon={faStar} className="text-gold text-xs" />);
      } else if (i === fullStars && hasHalfStar) {
        stars.push(<FontAwesomeIcon key={i} icon={faStarHalfAlt} className="text-gold text-xs" />);
      } else {
        stars.push(<FontAwesomeIcon key={i} icon={faStarRegular} className="text-gold text-xs" />);
      }
    }
    return stars;
  };

  const handleOpenModal = (cocktail) => {
    setSelectedCocktail(cocktail);
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSelectedCocktail(null);
  };

  const handleSave = (drinkid, updatedInLists) => {
    // Update the cocktails state with the new in_lists for the cocktail
    setCocktails((prevCocktails) =>
      prevCocktails
        .map((cocktail) =>
          cocktail.drinkid === drinkid ? { ...cocktail, in_lists: updatedInLists } : cocktail
        )
        .filter((cocktail) => {
          // If viewing a specific list (listid is defined), only keep cocktails that are still in this list
          if (listid) {
            const currentListId = parseInt(listid);
            // Special case for "Favorites" (listid 0)
            if (currentListId === 0) {
              // For Favorites, keep the cocktail if it’s in any list
              return cocktail.in_lists && cocktail.in_lists.length > 0;
            }
            // For other lists, keep the cocktail if its in_lists includes the current listid
            return cocktail.in_lists && cocktail.in_lists.includes(currentListId);
          }
          // If not viewing a specific list (e.g., /cocktails/type/:typeid), keep all cocktails
          return true;
        })
    );
  };

  if (loading) {
    return <div className="flex justify-center items-center h-screen">Loading...</div>;
  }

  if (error) {
    return <div className="text-red-500 text-center">{error}</div>;
  }

  return (
    <div className="cocktails-container">
      <div className="cocktails-gradient-bg"></div>
      <div className="cocktails-content-container">
        <Header
          showBack={true}
          title={listName}
          subtitle={`${cocktails.length} recipes`}
          showProfile={true}
          onLogout={onLogout}
          onBack={() => navigate(typeid ? '/spirit' : '/lists')}
        />

        <div className="search-section">
          <div className="search-bar">
            <FontAwesomeIcon icon={faSearch} className="search-icon" />
            <input type="text" placeholder="Search cocktails" className="search-input" />
            <button className="filter-btn">
              <FontAwesomeIcon icon={faSlidersH} />
            </button>
          </div>
          <div className="category-pills">
            <button className="category-pill active">All</button>
            <button className="category-pill">Gin</button>
            <button className="category-pill">Vodka</button>
            <button className="category-pill">Rum</button>
            <button className="category-pill">Tequila</button>
          </div>
        </div>

        <div className="cocktail-list">
          {cocktails.map((cocktail) => (
            <div
              key={cocktail.drinkid}
              className="card cocktail-card rounded-xl overflow-hidden flex"
              onClick={() => navigate(`/cocktail/${cocktail.drinkid}`)}
              style={{ cursor: 'pointer' }}
            >
              <div className="cocktail-card-inner">
                <div className="w-28 h-32 overflow-hidden flex-shrink-0">
                  <img
                    src={`${IMAGE_BASE_URL}${cocktail.picture}`}
                    alt={cocktail.name}
                    className="w-full h-full object-cover"
                  />
                </div>
                <div className="flex-1 p-4 flex flex-col">
                  <div>
                    <div className="flex justify-between items-start mb-1">
                      <h3 className="font-semibold text-lg m-0">{cocktail.name}</h3>
                      {cocktail.base_liquor && cocktail.base_liquor[0] && (
                        <span className="bg-primary/20 text-primary text-xs px-2 py-1 rounded-md">
                          {cocktail.base_liquor[0]}
                        </span>
                      )}
                    </div>
                    <div className="flex items-center cocktail-card-star-rating">
                      <div className="flex text-gold text-xs mr-2">
                        {renderStars(parseFloat(cocktail.rating))}
                      </div>
                      <span className="text-xs text-gray-400">
                        {cocktail.rating && cocktail.rating !== '0'
                          ? `(${cocktail.num_ratings})`
                          : cocktail.num_ratings}
                      </span>
                    </div>
                    <p className="text-xs text-gray-300 line-clamp-2 short-ingredients">
                      {cocktail.shortingredients}
                    </p>
                  </div>
                  <div className="flex justify-between items-center mt-2">
                    <span className="text-xs text-gray-400 flex items-center">
                      <FontAwesomeIcon icon={faClock} className="mr-1" />
                      {cocktail.minutes_to_make} min
                    </span>
                    <button
                      className="w-8 h-8 rounded-full bg-dark-700 flex items-center justify-center relative"
                      onClick={(e) => {
                        e.stopPropagation(); // Prevent navigating to the cocktail detail page
                        handleOpenModal(cocktail);
                      }}
                    >
                      <FontAwesomeIcon
                        icon={faBookmark}
                        className={
                          cocktail.in_lists && cocktail.in_lists.length > 0
                            ? 'text-primary'
                            : 'text-gray-300'
                        }
                      />
                    </button>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
        {/* Add to List Modal */}
        <AddToListModal
          isOpen={isModalOpen}
          onClose={handleCloseModal}
          cocktail={selectedCocktail || {}}
          lists={lists}
          apiClient={apiClient}
          userData={userData}
          onSave={handleSave}
          onUpdateLists={onUpdateLists}
        />
      </div>
    </div>
  );
}

export default CocktailsListPage;